import { SpacesIcon } from '@app/components/icons/spaces';
import { FLAG_SPACES } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import {
  selectCurrentSpace,
  selectOrgSpaces,
  selectOrgSpacesStatus,
} from '@app/routes/organization/selectors';
import {
  selectIsOrgSelected,
  selectIsOrgSwitcherOpen,
  selectNavBarSelectedOrgInfo,
  selectOrganizationsStatus,
  selectUserProfile,
  selectVendorBoothsStatus,
} from '@app/store/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { OrgNameLoadingSkelton } from './component/list-loading-skelton';
import { InvalidSpaceAccessModal } from './invalid-space-access-modal';
import { OrgSwitcherMenu } from './org-switcher-menu';
import type { OrgSwitcherProps } from './types';

export const OrgSwitcher = ({
  manageOrg,
  manageProfile,
  onSelectOrg,
  onSelectProfile,
  onLogOut,
  onCreateEvent,
  onCreateOrg,
  onSelectVendorBooth,
}: OrgSwitcherProps) => {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const isSpacesEnabled = flags?.[FLAG_SPACES] ?? false;
  const userProfile = useAppSelector(selectUserProfile);
  const { firstName, lastName, avatarImage, largeAvatarImage } =
    userProfile || {};
  const organizationsLoadingStatus = useAppSelector(selectOrganizationsStatus);
  const vendorBoothsLoadingStatus = useAppSelector(selectVendorBoothsStatus);
  const orgSpacesStatus = useAppSelector(selectOrgSpacesStatus);
  const spaces = useAppSelector(selectOrgSpaces);
  const currentSpace = useAppSelector(selectCurrentSpace);
  const isOrgSelected = useAppSelector(selectIsOrgSelected);
  const isOrgSwitcherOpen = useAppSelector(selectIsOrgSwitcherOpen);
  const selectedOrgInfo = useAppSelector(selectNavBarSelectedOrgInfo);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const requestIdleCallbackId = useRef<number | null>(null);
  const [wasOpenedByKeyboard, setWasOpenedByKeyboard] = useState(false);
  const { t } = useTranslation();

  // only surface space context when the
  // user can actually switch between more than one space.
  const showSelectedSpaceName =
    isSpacesEnabled &&
    isOrgSelected &&
    Boolean(currentSpace.name) &&
    (spaces?.length ?? 0) > 1;

  const resetOrgSwitcherOpenState = useCallback(() => {
    dispatch({
      type: '[nav-bar] update org switcher open state',
      payload: { isOrgSwitcherOpen: false },
    });
    setWasOpenedByKeyboard(false);
  }, [dispatch]);

  const cancelIdleCallback = useCallback(() => {
    if (requestIdleCallbackId.current && 'cancelIdleCallback' in window) {
      window.cancelIdleCallback(requestIdleCallbackId.current);
      requestIdleCallbackId.current = null;
    }
  }, []);

  const onClose = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(e.target as Node) &&
        contentRef.current &&
        !contentRef.current.contains(e.target as Node)
      ) {
        resetOrgSwitcherOpenState();
      }
    },
    [resetOrgSwitcherOpenState, toggleRef, contentRef],
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      onClose(event);
    },
    [onClose],
  );

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        resetOrgSwitcherOpenState();
      }
    },
    [resetOrgSwitcherOpenState],
  );

  const fetchOrgAndVendorBoothsData = useCallback(() => {
    if (organizationsLoadingStatus === 'idle') {
      dispatch({ type: '[nav-bar] organizations fetch' });
    }
    if (vendorBoothsLoadingStatus === 'idle') {
      dispatch({ type: '[nav-bar] vendor-booths fetch' });
    }
  }, [organizationsLoadingStatus, vendorBoothsLoadingStatus, dispatch]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    // Trigger lazy loading before opening the menu on requestIdleCallback
    if (window?.requestIdleCallback) {
      requestIdleCallbackId.current = window.requestIdleCallback(() => {
        fetchOrgAndVendorBoothsData();
        cancelIdleCallback();
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      cancelIdleCallback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileButtonClick = () => {
    const newIsOpen = !isOrgSwitcherOpen;
    dispatch({
      type: '[nav-bar] update org switcher open state',
      payload: { isOrgSwitcherOpen: newIsOpen },
    });

    // Trigger lazy loading when opening the menu
    if (newIsOpen) {
      fetchOrgAndVendorBoothsData();
      if (isSpacesEnabled && orgSpacesStatus !== 'processing') {
        dispatch({
          type: '[organization] fetch org spaces',
        });
      }
      if (isSpacesEnabled) {
        dispatch({
          type: '[organization] validate current space access',
        });
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setWasOpenedByKeyboard(true);
        profileButtonClick();
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative">
      <button
        ref={toggleRef}
        type="button"
        className="flex items-center h-full px-3 py-2 text-sm font-medium text-gray-700 bg-white border-none rounded-none outline-none hover:bg-[rgb(236,243,248)] hover:rounded-none focus:bg-[rgb(236,243,248)] focus:rounded-none focus:outline-none"
        onClick={() => {
          setWasOpenedByKeyboard(false);
          profileButtonClick();
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="true"
        aria-expanded={isOrgSwitcherOpen}
        aria-label={
          isOrgSelected && selectedOrgInfo?.name
            ? t(
                showSelectedSpaceName
                  ? 'orgSwitcher.accessibility.switchOrganizationWithSpace'
                  : 'orgSwitcher.accessibility.switchOrganization',
                {
                  organizationName: selectedOrgInfo.name,
                  spaceName: currentSpace.name,
                },
              )
            : t('orgSwitcher.accessibility.openOrganizationSwitcher')
        }
      >
        {isOrgSelected && selectedOrgInfo?.name && (
          <>
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-medium mr-2">
              {selectedOrgInfo.image ? (
                <img
                  src={selectedOrgInfo.image}
                  alt={selectedOrgInfo.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                selectedOrgInfo.name?.charAt(0)?.toUpperCase()
              )}
            </div>
            <div className="max-w-[250px] mr-6 pr-6 border-r border-r-[rgb(228,230,235)] min-w-0 text-left">
              <div
                className="text-gray-800 font-medium truncate overflow-hidden whitespace-nowrap"
                title={selectedOrgInfo.name}
              >
                {selectedOrgInfo.name}
              </div>
              {showSelectedSpaceName && (
                <div className="flex min-w-0 items-center gap-1 text-xs text-[rgb(105,110,124)]">
                  <SpacesIcon className="flex-shrink-0" aria-hidden="true" />
                  <span
                    className="min-w-0 truncate overflow-hidden whitespace-nowrap"
                    title={currentSpace.name}
                  >
                    {currentSpace.name}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
        {isOrgSelected && !selectedOrgInfo.name && <OrgNameLoadingSkelton />}
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-medium">
          {largeAvatarImage || avatarImage ? (
            <img
              src={largeAvatarImage || avatarImage}
              alt={`${firstName || ''} ${lastName || ''}`}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`
          )}
        </div>
        {!isOrgSelected && (
          <span className="ml-2 text-gray-800 font-medium mr-6 pr-6 border-r border-r-[rgb(228,230,235)] truncate overflow-hidden whitespace-nowrap">
            {firstName || ''} {lastName || ''}
          </span>
        )}
        <svg
          className={`ml-2 h-4 w-4 text-gray-400 transition-transform ${
            isOrgSwitcherOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOrgSwitcherOpen && (
        <div
          ref={contentRef}
          className="absolute right-0 mt-2 z-50"
          style={{ top: '57px' }}
          role="dialog"
          aria-modal="true"
          aria-label={t('orgSwitcher.accessibility.organizationSwitcherMenu')}
        >
          <OrgSwitcherMenu
            wasOpenedByKeyboard={wasOpenedByKeyboard}
            manageOrg={() => {
              manageOrg();
              resetOrgSwitcherOpenState();
            }}
            manageProfile={() => {
              manageProfile();
              resetOrgSwitcherOpenState();
            }}
            onLanguageClickHandler={() => {
              dispatch({
                type: '[nav-bar] update language picker open state',
                payload: { isLanguagePickerOpen: true },
              });
              resetOrgSwitcherOpenState();
            }}
            onSelectOrg={selectedOrg => {
              onSelectOrg(selectedOrg);
              resetOrgSwitcherOpenState();
            }}
            onSelectProfile={() => {
              onSelectProfile();
              resetOrgSwitcherOpenState();
            }}
            onLogOut={() => {
              onLogOut();
              resetOrgSwitcherOpenState();
            }}
            onCreateEvent={() => {
              onCreateEvent();
              resetOrgSwitcherOpenState();
            }}
            onCreateOrg={() => {
              onCreateOrg();
              resetOrgSwitcherOpenState();
            }}
            onSelectVendorBooth={vendorBooth => {
              onSelectVendorBooth(vendorBooth);
              resetOrgSwitcherOpenState();
            }}
          />
        </div>
      )}
      <InvalidSpaceAccessModal />
    </div>
  );
};
